import { environment } from '../../environments/environment';
import { Observable} from 'rxjs';
import { Merchant } from '../Merchant';
import { HeaderService } from './header.service';
import { AuthService } from './auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, Output, EventEmitter } from '@angular/core';
import { CustomerAccount } from '../data/CustomerAccount';

const RECENT_MERCHANTS = 'recent_merchants';
const SELECTED_MERCHANT = 'selected_merchant';
const BASEURL = `${environment.rootApiUrl}`;

@Injectable({
  providedIn: 'root'
})
export class MerchantService {
  selectedShip: string = null;
  passedParams: any = {};

  @Output() merchantSelected: EventEmitter<Merchant> = new EventEmitter();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private headerService: HeaderService
  ) {
    this.authService.loginStatusChanged.subscribe(loginStatus => {
      if (!loginStatus) {
        this.clearStoredMerchants();
        this.selectedShip = null;
      }
    });
  }

  clearPassedParams() {
    this.passedParams = {};
  }

  getPassedParams() {
    return this.passedParams;
  }

  getPassedParam(paramName: string) {
    const returnVal = this.passedParams[paramName];
    return returnVal === null || returnVal === undefined ? '' : returnVal;
  }

  setPassedParam(name: string, value: string) {
    this.passedParams[name] = value;
  }

  getMerchants(ship: string, searchString: string): Observable<Merchant[]> {
    const url = BASEURL + '/merchants/merchantsearch/search';
    let params = new HttpParams();
    params = params.append('firstParameter', searchString || '');
    params = params.append('ship', ship || '');

    return this.http.post<Merchant[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getRecentMerchants(): Merchant[] {
    const recentMerchants = window.sessionStorage.getItem(RECENT_MERCHANTS);
    let parsedMerchants: Merchant[] = [];
    if (recentMerchants) {
      parsedMerchants = JSON.parse(recentMerchants);
    }
    return parsedMerchants;
  }

  getMerchantData(merchantNumber: number, ship: string): Observable<any> {
    const url =
      BASEURL +
      '/merchants/merchantsearch/profile/' +
      merchantNumber +
      '/' +
      ship;
    return this.http.get<any>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  updateCardLimit(acctNumber: number, limit: number): Observable<any> {
    const url = BASEURL + '/merchants/cardLimit/update';
    let params = new HttpParams();
    params = params.append('acctNumber', acctNumber || '');
    params = params.append('cardLimit', limit || '');
    return this.http.post<Merchant[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getCardLimit(acctNumber: number): Observable<any> {
    const url =
      BASEURL +
      '/merchants/cardLimit/read/' +
      acctNumber;
    return this.http.get<any>(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }


  getMerchantAchFundingInfo(
    UIC: string,
    ncMerchantNum: number
  ): Observable<any> {
    const url = BASEURL + '/merchants/achfunding/read';
    let params = new HttpParams();
    params = params.append('UIC', UIC);
    params = params.append('ncMerchantNum', '' + ncMerchantNum);

    return this.http.get(url, { headers: this.headerService.getXAuthHeaderFromCookie(), params: params });
  }

  getMerchantTransactions(
    payrollAccount: number,
    chipAccount: string,
    startDate: string = null,
    endDate: string = null
  ): Observable<any> {
    const url =
      BASEURL +
      '/common/merchants/transactions/' +
      payrollAccount +
      '/' +
      chipAccount;
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);

    return this.http.post<Merchant[]>(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  formatDate(dateIn: Date) {
    return (
      dateIn.getMonth() +
      1 +
      '/' +
      dateIn.getDate() +
      '/' +
      dateIn.getFullYear()
    );
  }

  clearStoredMerchants() {
    window.sessionStorage.removeItem(RECENT_MERCHANTS);
    window.sessionStorage.removeItem(SELECTED_MERCHANT);
  }

  storeRecentMerchant(merchant: Merchant) {
    // Store the merchant if it is not already stored
    const merchants = this.getRecentMerchants();
    let found = false;
    merchants.forEach(elem => {
      if (elem.ncMerchantNum === merchant.ncMerchantNum) {
        found = true;
      }
    });
    if (!found) {
      merchants.push(merchant);
      window.sessionStorage.setItem(
        RECENT_MERCHANTS,
        JSON.stringify(merchants)
      );
    }
  }

  selectMerchant(merchant: Merchant) {
    window.sessionStorage.setItem(SELECTED_MERCHANT, JSON.stringify(merchant));
    this.merchantSelected.emit(merchant);
  }

  getSelectedMerchant(): Merchant {
    const selectedMerchant = window.sessionStorage.getItem(SELECTED_MERCHANT);
    let parsedMerchant: Merchant = null;
    if (selectedMerchant) {
      parsedMerchant = JSON.parse(selectedMerchant);
    }
    return parsedMerchant;
  }

  saveStripAccount(merchNum: number, shipLoc: string, stripAccount: object) {
    const url =
      BASEURL +
      '/merchants/stripAccount/' +
      merchNum +
      '/' +
      shipLoc +
      '/update';
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, stripAccount, {
      headers: headers,
      responseType: 'text'
    });
  }

  saveAccount(merchNum: number, shipLoc: string, card: object) {
    const url =
      BASEURL +
      '/merchants/updateChipCard/' +
      merchNum +
      '/' +
      shipLoc +
      '/update';
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, card, {
      headers: headers,
      responseType: 'text'
    });
  }


  saveAccountableOfficial(
    payrollAcctNum: number,
    merchNum: number,
    shipLoc: string
  ): Observable<string> {
    const url =
      BASEURL +
      '/merchants/updateAccountableOfficial/' +
      merchNum +
      '/' +
      shipLoc +
      '/update';
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, payrollAcctNum, {
      headers: headers,
      responseType: 'text'
    });
  }

  applyAdjustment(passedParams: object) {
    const url = BASEURL + '/common/adjustments/balance_adj';

    let params = new HttpParams();
    params = params.append('acctNum', passedParams['accountNum'].toString());
    params = params.append('adjustReason', passedParams['adjustReason']);
    params = params.append('adjustAmt', passedParams['adjustAmt']);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  loadAdjustments() {
    const url = BASEURL + '/common/adjustments/read';

    return this.http.get(url, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  balanceTransfer(passedParams: object) {
    const url = BASEURL + '/common/adjustments/balance_xfer';

    let params = new HttpParams();
    params = params.append('srcAcct', passedParams['srcAcct']);
    params = params.append('desAcct', passedParams['desAcct']);
    params = params.append('xferAmt', passedParams['xferAmt']);

    return this.http.post(url, params, {
      headers: this.headerService.getXAuthHeaderFromCookie(),
      responseType: 'text'
    });
  }

  getTransHistory(startDate, endDate, account, type) {
    const url =
      BASEURL + '/merchants/adjustmentTransactions/' + account + '/' + type;
    let params = new HttpParams();
    params = params.append('startDate', startDate);
    params = params.append('endDate', endDate);

    return this.http.post(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  getSettlementDetails(merchNum: string, shipLoc: string) {
    const url = BASEURL + '/merchants/settlement/read';
    let params = new HttpParams();
    params = params.append('merchantNumber', merchNum);
    params = params.append('shipUIC', shipLoc);

    return this.http.get(url, { headers: this.headerService.getXAuthHeaderFromCookie(), params: params });
  }

  loadSettlementHistory(
    merchNum: string,
    shipLoc: string,
    sDate: string,
    eDate: string
  ) {
    const url = BASEURL + '/merchants/settlement/history';
    let params = new HttpParams();
    params = params.append('ncMerchantNum', merchNum);
    params = params.append('shipUIC', shipLoc);
    params = params.append('startDate', sDate);
    params = params.append('endDate', eDate);

    return this.http.post(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  saveMerchantSettlement(
    merchNum: string,
    shipLoc: string,
    autoSettleType: string,
    autoSettleSchedule: string
  ) {
    const url = BASEURL + '/merchants/settlement/save';
    let params = new HttpParams();
    params = params.append('merchantNumber', merchNum);
    params = params.append('shipUIC', shipLoc);
    params = params.append('autoSettleType', autoSettleType);
    params = params.append('autoSettleSchedule', autoSettleSchedule);

    return this.http.post(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  showAchRequestHistory(payRoll: string, sDate: string, eDate: string) {
    const url = BASEURL + '/common/achfunding/history';
    let params = new HttpParams();
    params = params.append('payrollAccount', payRoll);
    params = params.append('startMonth', sDate);
    params = params.append('endMonth', eDate);

    return this.http.post(url, params, this.headerService.getHttpOptionsXAuthHeaderFromCookie());
  }

  updateACHFundingInfo(
    ncMerchNum: string,
    location: string,
    fundingInfo: object
  ) {
    const url =
      BASEURL + '/merchants/achfunding/update/' + ncMerchNum + '/' + location;
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, fundingInfo, {
      headers: headers,
      responseType: 'text'
    });
  }

  syncAccount(account: CustomerAccount) {
    const url = BASEURL + '/merchants/account/sync';
    let headers = this.headerService.getXAuthHeaderFromCookie();
    headers = headers.set('Content-Type', 'application/json');

    return this.http.post(url, account, { headers: headers, responseType: 'text' });
  }

  getAccountableOfficial(merchantNumber: any) {
    const url = BASEURL + '/merchants/accountableOfficial';
    let params = new HttpParams();
    params = params.append('merchantNumber', merchantNumber);
    return this.http.get(url, { headers: this.headerService.getXAuthHeaderFromCookie(), params: params, responseType: "text"});
  }

  getAccountableOfficialsMerchants(lastFourSSN: string, firstName: String, lastName: String) {
    const url = BASEURL + '/merchants/accountableOfficial/merchantAccounts';
    let params = new HttpParams();
    params = params.append('lastFourSSN', lastFourSSN);
    params = params.append('firstName', firstName.toString());
    params = params.append('lastName', lastName.toString());
    return this.http.get(url, { headers: this.headerService.getXAuthHeaderFromCookie(), params: params});
  }

}
